// Viewer State Store v1.0.0
import { create } from 'zustand'
import type {
  CameraChannel,
  ClipEventType,
  TimelineSegment,
  MaskAuthorization,
  VideoClip,
  DetectionFrame,
} from '../types'

interface ViewerStore {
  // Time navigation
  currentTime: Date
  playbackSpeed: number
  isPlaying: boolean
  setCurrentTime: (time: Date) => void
  setPlaybackSpeed: (speed: number) => void
  togglePlayback: () => void
  play: () => void
  pause: () => void

  // Channel selection
  selectedChannels: CameraChannel[]
  layout: 'single' | 'grid' | 'pip'
  primaryChannel: CameraChannel
  toggleChannel: (channel: CameraChannel) => void
  setLayout: (layout: 'single' | 'grid' | 'pip') => void
  setPrimaryChannel: (channel: CameraChannel) => void
  selectAllChannels: () => void
  selectSingleChannel: (channel: CameraChannel) => void

  // Timeline zoom/pan
  timelineStart: Date
  timelineEnd: Date
  zoomLevel: number // 1 = 24h, 2 = 12h, 4 = 6h, 8 = 3h, 16 = 1.5h, 32 = 45min
  setTimelineRange: (start: Date, end: Date) => void
  setZoomLevel: (level: number) => void
  zoomIn: () => void
  zoomOut: () => void
  panLeft: () => void
  panRight: () => void
  goToTime: (time: Date) => void

  // Data
  segments: TimelineSegment[]
  currentClips: Map<CameraChannel, VideoClip | null>
  detectionFrames: Map<CameraChannel, DetectionFrame | null>
  setSegments: (segments: TimelineSegment[]) => void
  setCurrentClip: (channel: CameraChannel, clip: VideoClip | null) => void
  setDetectionFrame: (channel: CameraChannel, frame: DetectionFrame | null) => void

  // Filters
  eventTypeFilter: ClipEventType[]
  threatLevelFilter: ('none' | 'low' | 'medium' | 'high' | 'critical')[]
  showOnlyWithDetections: boolean
  toggleEventTypeFilter: (type: ClipEventType) => void
  toggleThreatLevelFilter: (level: 'none' | 'low' | 'medium' | 'high' | 'critical') => void
  setShowOnlyWithDetections: (show: boolean) => void

  // Child mask protection
  maskChildDetections: boolean
  activeMaskAuthorizations: MaskAuthorization[]
  setMaskChildDetections: (mask: boolean) => void
  addMaskAuthorization: (auth: MaskAuthorization) => void
  removeMaskAuthorization: (authId: string) => void
  checkMaskAuthorization: (clipId: string) => MaskAuthorization | null

  // UI state
  showSidebar: boolean
  showDetectionOverlay: boolean
  showTimestamps: boolean
  selectedDetectionId: string | null
  toggleSidebar: () => void
  toggleDetectionOverlay: () => void
  toggleTimestamps: () => void
  setSelectedDetection: (id: string | null) => void

  // Loading states
  isLoading: boolean
  loadingMessage: string
  setLoading: (loading: boolean, message?: string) => void
}

const ALL_CHANNELS: CameraChannel[] = ['front', 'back', 'left_repeater', 'right_repeater']
const ALL_EVENT_TYPES: ClipEventType[] = ['SentryMode', 'SavedClip', 'RecentClips', 'Honk', 'UserInteraction']

// Default to a 30-day window ending now so older clips remain discoverable/searchable
const now = new Date()
const thirtyDaysMs = 30 * 24 * 60 * 60 * 1000
const windowStart = new Date(now.getTime() - thirtyDaysMs)
const windowEnd = now

export const useViewerStore = create<ViewerStore>()((set, get) => ({
    // Time navigation
    currentTime: now,
    playbackSpeed: 1,
    isPlaying: false,
    setCurrentTime: (time) => set({ currentTime: time }),
    setPlaybackSpeed: (speed) => set({ playbackSpeed: speed }),
    togglePlayback: () => set((state) => ({ isPlaying: !state.isPlaying })),
    play: () => set({ isPlaying: true }),
    pause: () => set({ isPlaying: false }),

    // Channel selection
    selectedChannels: ALL_CHANNELS,
    layout: 'grid',
    primaryChannel: 'front',
    toggleChannel: (channel) =>
      set((state) => {
        const channels = state.selectedChannels.includes(channel)
          ? state.selectedChannels.filter((c) => c !== channel)
          : [...state.selectedChannels, channel]
        // Ensure at least one channel is selected
        if (channels.length === 0) return state
        // Update primary if it was deselected
        const primary = channels.includes(state.primaryChannel)
          ? state.primaryChannel
          : channels[0]
        return { selectedChannels: channels, primaryChannel: primary }
      }),
    setLayout: (layout) => set({ layout }),
    setPrimaryChannel: (channel) =>
      set((state) => {
        if (!state.selectedChannels.includes(channel)) {
          return { selectedChannels: [...state.selectedChannels, channel], primaryChannel: channel }
        }
        return { primaryChannel: channel }
      }),
    selectAllChannels: () => set({ selectedChannels: ALL_CHANNELS }),
    selectSingleChannel: (channel) => set({ selectedChannels: [channel], primaryChannel: channel, layout: 'single' }),

    // Timeline zoom/pan
    timelineStart: windowStart,
    timelineEnd: windowEnd,
    zoomLevel: 1,
    setTimelineRange: (start, end) => set({ timelineStart: start, timelineEnd: end }),
    setZoomLevel: (level) => {
      const state = get()
      const centerTime = state.currentTime.getTime()
      const totalMs = 24 * 60 * 60 * 1000 / level
      const halfMs = totalMs / 2
      set({
        zoomLevel: level,
        timelineStart: new Date(centerTime - halfMs),
        timelineEnd: new Date(centerTime + halfMs),
      })
    },
    zoomIn: () => {
      const state = get()
      if (state.zoomLevel < 64) {
        state.setZoomLevel(state.zoomLevel * 2)
      }
    },
    zoomOut: () => {
      const state = get()
      if (state.zoomLevel > 1) {
        state.setZoomLevel(state.zoomLevel / 2)
      }
    },
    panLeft: () => {
      const state = get()
      const rangeMs = state.timelineEnd.getTime() - state.timelineStart.getTime()
      const panMs = rangeMs * 0.25
      set({
        timelineStart: new Date(state.timelineStart.getTime() - panMs),
        timelineEnd: new Date(state.timelineEnd.getTime() - panMs),
      })
    },
    panRight: () => {
      const state = get()
      const rangeMs = state.timelineEnd.getTime() - state.timelineStart.getTime()
      const panMs = rangeMs * 0.25
      set({
        timelineStart: new Date(state.timelineStart.getTime() + panMs),
        timelineEnd: new Date(state.timelineEnd.getTime() + panMs),
      })
    },
    goToTime: (time) => {
      const state = get()
      const rangeMs = state.timelineEnd.getTime() - state.timelineStart.getTime()
      const halfMs = rangeMs / 2
      set({
        currentTime: time,
        timelineStart: new Date(time.getTime() - halfMs),
        timelineEnd: new Date(time.getTime() + halfMs),
      })
    },

    // Data
    segments: [],
    currentClips: new Map(),
    detectionFrames: new Map(),
    setSegments: (segments) => set({ segments }),
    setCurrentClip: (channel, clip) =>
      set((state) => {
        const newClips = new Map(state.currentClips)
        newClips.set(channel, clip)
        return { currentClips: newClips }
      }),
    setDetectionFrame: (channel, frame) =>
      set((state) => {
        const newFrames = new Map(state.detectionFrames)
        newFrames.set(channel, frame)
        return { detectionFrames: newFrames }
      }),

    // Filters
    eventTypeFilter: ALL_EVENT_TYPES,
    threatLevelFilter: ['none', 'low', 'medium', 'high', 'critical'],
    showOnlyWithDetections: false,
    toggleEventTypeFilter: (type) =>
      set((state) => {
        const filters = state.eventTypeFilter.includes(type)
          ? state.eventTypeFilter.filter((t) => t !== type)
          : [...state.eventTypeFilter, type]
        return { eventTypeFilter: filters.length > 0 ? filters : [type] }
      }),
    toggleThreatLevelFilter: (level) =>
      set((state) => {
        const filters = state.threatLevelFilter.includes(level)
          ? state.threatLevelFilter.filter((l) => l !== level)
          : [...state.threatLevelFilter, level]
        return { threatLevelFilter: filters.length > 0 ? filters : [level] }
      }),
    setShowOnlyWithDetections: (show) => set({ showOnlyWithDetections: show }),

    // Child mask protection - ALWAYS ON BY DEFAULT
    maskChildDetections: true,
    activeMaskAuthorizations: [],
    setMaskChildDetections: (mask) => set({ maskChildDetections: mask }),
    addMaskAuthorization: (auth) =>
      set((state) => ({
        activeMaskAuthorizations: [...state.activeMaskAuthorizations, auth],
      })),
    removeMaskAuthorization: (authId) =>
      set((state) => ({
        activeMaskAuthorizations: state.activeMaskAuthorizations.filter((a) => a.id !== authId),
      })),
    checkMaskAuthorization: (clipId) => {
      const state = get()
      const auth = state.activeMaskAuthorizations.find(
        (a) => a.clipId === clipId && a.status === 'approved' && (!a.expiresAt || new Date(a.expiresAt) > new Date())
      )
      return auth || null
    },

    // UI state
    showSidebar: true,
    showDetectionOverlay: true,
    showTimestamps: true,
    selectedDetectionId: null,
    toggleSidebar: () => set((state) => ({ showSidebar: !state.showSidebar })),
    toggleDetectionOverlay: () => set((state) => ({ showDetectionOverlay: !state.showDetectionOverlay })),
    toggleTimestamps: () => set((state) => ({ showTimestamps: !state.showTimestamps })),
    setSelectedDetection: (id) => set({ selectedDetectionId: id }),

    // Loading states
    isLoading: false,
    loadingMessage: '',
    setLoading: (loading, message = '') => set({ isLoading: loading, loadingMessage: message }),
}))

// Selector hooks for performance
export const useCurrentTime = () => useViewerStore((s) => s.currentTime)
export const useIsPlaying = () => useViewerStore((s) => s.isPlaying)
export const useSelectedChannels = () => useViewerStore((s) => s.selectedChannels)
export const useLayout = () => useViewerStore((s) => s.layout)
export const useZoomLevel = () => useViewerStore((s) => s.zoomLevel)
export const useMaskChildDetections = () => useViewerStore((s) => s.maskChildDetections)
